import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { usePartners } from '../../../hooks/api/usePartners';
import {
  setAssumedRoles,
  PARTNER_ASSUME_SCOPE,
} from '../../../api/assumedRoles';
import { userSessionStore } from '../../../utils/userStorage';
import type { components } from '../../../api/generated/hsOfficeApi';
import type { Stored } from '../../../api/stored';

type Partner = Stored<components['schemas']['HsOfficePartner']>;

type PartnerSelection = {
  partners: Partner[];
  selectedPartner: Partner | null;
  allPartnersSelected: boolean;
  selectPartner: (uuid: string) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
};

/*
 * Everything in the dashboard below the sidebar selector refers to the
 * selected partner. The choice survives a reload in the browser, for
 * this tab and this logged-in user (see utils/userStorage);
 * with only one visible partner it is effectively fixed. With several
 * visible partners "all partners" can be chosen (and is the default):
 * `selectedPartner` is null then and `allPartnersSelected` is true.
 *
 * Selecting a concrete partner also assumes the admin role of its
 * holder person for the office/accounts endpoints and the project list
 * (see assumedRoles.ts); "all partners" runs unassumed.
 */
const STORAGE_KEY = 'selectedPartnerUuid';

/* there are no roles on the partner object itself — selecting a
   partner assumes the ADMIN role of the holder person of its PARTNER
   relation, the one role the backend documents for the whole partner
   view: relations incl. subscriptions, membership, coop shares/assets
   and (via its relation-AGENT sub-role) the embedded partner details.
   A relation without a holder cannot exist (DB constraint), so
   guarding the relation itself is enough. */
export const partnerAssumedRoles = (partner: Partner) =>
  partner.partnerRel?.uuid
    ? `hs_office.person#${partner.partnerRel.holder!.uuid}:ADMIN`
    : null;

/* query key roots of the areas the person-admin role is sent to;
   their cached answers were fetched under the previous assume. The
   partner list itself always stays unassumed and keeps its cache. */
const PARTNER_SCOPED_QUERY_KEYS = [
  ['contact'],
  ['person'],
  ['relation'],
  ['memberships'],
  ['debitors'],
  ['sepaMandates'],
  ['account'],
  ['booking', 'projects'],
  ['assets'],
  ['projects'],
  /* single-partner GETs (the role decides whether the embedded
     details are filled) — NOT ['partners'], the unassumed list */
  ['partner'],
];

export const ALL_PARTNERS = 'all';

const PartnerContext = createContext<PartnerSelection | null>(null);

export function PartnerProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError, error } = usePartners();
  const partners = useMemo(
    () =>
      (Array.isArray(data) ? [...data] : []).sort((a, b) =>
        String(a.partnerNumber).localeCompare(
          String(b.partnerNumber),
          undefined,
          {
            numeric: true,
          }
        )
      ),
    [data]
  );

  const [selectedUuid, setSelectedUuid] = useState(() =>
    userSessionStore.getItem(STORAGE_KEY)
  );

  const selectPartner = (uuid: string) => {
    setSelectedUuid(uuid);
    userSessionStore.setItem(STORAGE_KEY, uuid);
  };

  /* without a concrete choice, several visible partners mean "all
     partners"; a single visible partner is effectively fixed */
  const concretePartner =
    partners.find((partner) => partner.uuid === selectedUuid) ?? null;
  const allPartnersSelected = partners.length > 1 && !concretePartner;
  const selectedPartner =
    concretePartner ?? (allPartnersSelected ? null : (partners[0] ?? null));

  const queryClient = useQueryClient();
  const selectedPartnerRole = selectedPartner
    ? partnerAssumedRoles(selectedPartner)
    : null;

  useEffect(() => {
    setAssumedRoles(PARTNER_ASSUME_SCOPE, selectedPartnerRole);
    for (const queryKey of PARTNER_SCOPED_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [selectedPartnerRole, queryClient]);

  return (
    <PartnerContext.Provider
      value={{
        partners,
        selectedPartner,
        allPartnersSelected,
        selectPartner,
        isLoading,
        isError,
        error,
      }}
    >
      {children}
    </PartnerContext.Provider>
  );
}

export function usePartnerSelection(): PartnerSelection {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error('usePartnerSelection requires a PartnerProvider');
  }
  return context;
}
